import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import {
  getPrimaryMenuItemsForRole,
  getSecondaryMenuItemsForRole,
  getRoleLabel,
} from '../../config/role-routes';
import { MenuItem } from '../../config/role-routes';

interface DashboardLayoutProps {
  children: ReactNode;
  title?: string;
}

function Icon({ path, className = 'w-5 h-5' }: { path: string; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

export function DashboardLayout({ children, title }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const { showInfo } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const primaryItems = useMemo(() => getPrimaryMenuItemsForRole(user?.role).slice(0, 4), [user?.role]);
  const secondaryItems = useMemo(() => getSecondaryMenuItemsForRole(user?.role), [user?.role]);
  const allItems = useMemo(
    () => [...getPrimaryMenuItemsForRole(user?.role), ...secondaryItems],
    [user?.role, secondaryItems]
  );

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      showInfo('Conexión restablecida');
    };
    const onOffline = () => {
      setIsOnline(false);
      showInfo('Sin conexión — trabajando offline');
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [showInfo]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/gestion/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    if (!confirm('¿Seguro que quieres cerrar sesión?')) return;
    await logout();
    navigate('/login', { replace: true });
  };

  const initials = (user?.name ?? 'U').trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="safe-top bg-slate-900 text-white sticky top-0 z-30">
        <div className="flex items-center gap-3 px-4 h-14">
          <button
            onClick={() => setDrawerOpen(true)}
            className="p-1 -ml-1 rounded-md active:bg-white/10"
            aria-label="Abrir menú"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold leading-tight truncate">{title ?? 'GranArqui'}</h1>
            <p className="text-[11px] text-white/60 truncate">{getRoleLabel(user?.role)}</p>
          </div>
          <span
            className={`inline-block w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-red-500'}`}
            title={isOnline ? 'En línea' : 'Offline'}
          />
          <Link
            to="/gestion/configuracion"
            className="p-1 rounded-md active:bg-white/10"
            aria-label="Configuración"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
          </Link>
        </div>
      </header>

      <main
        className="flex-1 overflow-y-auto pb-24 px-4 pt-4"
        style={{ paddingBottom: 'calc(6rem + env(safe-area-inset-bottom))' }}
      >
        {children}
      </main>

      <nav
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <ul className="grid" style={{ gridTemplateColumns: `repeat(${primaryItems.length + 1}, 1fr)` }}>
          {primaryItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                  isActive(item.path) ? 'text-primary' : 'text-slate-500'
                }`}
              >
                <Icon path={item.icon} className="w-5 h-5" />
                <span>{item.shortLabel ?? item.label}</span>
              </Link>
            </li>
          ))}
          <li>
            <button
              onClick={() => setDrawerOpen(true)}
              className="w-full flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium text-slate-500"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span>Más</span>
            </button>
          </li>
        </ul>
      </nav>

      {drawerOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            className="absolute top-0 left-0 h-full w-72 bg-white shadow-2xl flex flex-col safe-top"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-lg">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{user?.name ?? 'Usuario'}</p>
                  <p className="text-xs text-slate-500 truncate">{getRoleLabel(user?.role)}</p>
                </div>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto py-2">
              <ul>
                {allItems.map((item: MenuItem) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-5 py-3 text-sm ${
                        isActive(item.path)
                          ? 'bg-primary/5 text-primary font-semibold'
                          : 'text-slate-700'
                      }`}
                    >
                      <Icon path={item.icon} className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="px-4 py-3 border-t border-slate-200">
              <button
                onClick={handleLogout}
                className="w-full py-2.5 rounded-lg bg-red-50 text-red-600 font-semibold text-sm active:bg-red-100"
              >
                Cerrar sesión
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}